import { TutorialStep } from './tutorial.service';

/**
 * Tutorial module definition.
 * Each module is a group of steps shown together (page-specific or feature-specific).
 */
export interface TutorialModule {
  id: string;
  /** Roles that can see this module. Empty = all roles. */
  roles?: string[];
  /** User types that can see this module (e.g. 'Paramedic', 'Caretaker'). Empty = all. */
  userTypes?: string[];
  /** Required privileges (any match). Empty = no privilege needed. */
  privileges?: string[];
  /** If true, only shown when user is individual (if_indiv_user). */
  requireIndividual?: boolean;
  /** Route prefix where this page-specific tutorial applies. */
  routePrefix?: string;
  /** Steps for this module. */
  steps: TutorialStep[];
}

// ============================================================
// ONBOARDING TUTORIALS (global, shown after first login)
// ============================================================

export const ONBOARDING_MANAGER: TutorialModule = {
  id: 'onboarding_manager',
  roles: ['ROLE_API_MANAGER'],
  steps: [
    {
      selector: '.dashboard-sidebar',
      titleKey: 'TUTORIAL.STEP_MENU_TITLE',
      descriptionKey: 'TUTORIAL.STEP_MENU_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="manager/dashboard"]',
      titleKey: 'TUTORIAL.STEP_HOME_TITLE',
      descriptionKey: 'TUTORIAL.STEP_HOME_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="caregivers"]',
      titleKey: 'TUTORIAL.STEP_CAREGIVERS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_CAREGIVERS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="users"]',
      titleKey: 'TUTORIAL.STEP_USERS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_USERS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="projects"]',
      titleKey: 'TUTORIAL.STEP_PROJECTS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_PROJECTS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="settings/reports"]',
      titleKey: 'TUTORIAL.STEP_REPORTS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_REPORTS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="my-dashboard"]',
      titleKey: 'TUTORIAL.STEP_NOTIFICATIONS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_NOTIFICATIONS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="manager/ai-paramedic"]',
      titleKey: 'TUTORIAL.STEP_AI_PARAMEDIC_TITLE',
      descriptionKey: 'TUTORIAL.STEP_AI_PARAMEDIC_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="voip/config"]',
      titleKey: 'TUTORIAL.STEP_VOIP_SETTINGS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_VOIP_SETTINGS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="voip/call-logs"]',
      titleKey: 'TUTORIAL.STEP_VOIP_HISTORY_TITLE',
      descriptionKey: 'TUTORIAL.STEP_VOIP_HISTORY_DESC',
      position: 'right'
    }
  ]
};

export const ONBOARDING_CAREGIVER: TutorialModule = {
  id: 'onboarding_caregiver',
  roles: ['ROLE_API_CAREGIVER'],
  steps: [
    {
      selector: '.dashboard-sidebar',
      titleKey: 'TUTORIAL.STEP_MENU_TITLE',
      descriptionKey: 'TUTORIAL.STEP_MENU_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="caregivers/dashboard-c"]',
      titleKey: 'TUTORIAL.STEP_HOME_TITLE',
      descriptionKey: 'TUTORIAL.STEP_HOME_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="users"]',
      titleKey: 'TUTORIAL.STEP_USERS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_USERS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="settings/reports"]',
      titleKey: 'TUTORIAL.STEP_REPORTS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_REPORTS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="my-dashboard"]',
      titleKey: 'TUTORIAL.STEP_NOTIFICATIONS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_NOTIFICATIONS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="manager/ai-paramedic"]',
      titleKey: 'TUTORIAL.STEP_AI_PARAMEDIC_TITLE',
      descriptionKey: 'TUTORIAL.STEP_AI_PARAMEDIC_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="voip/config"]',
      titleKey: 'TUTORIAL.STEP_VOIP_SETTINGS_TITLE',
      descriptionKey: 'TUTORIAL.STEP_VOIP_SETTINGS_DESC',
      position: 'right'
    },
    {
      selector: '[data-route="voip/call-logs"]',
      titleKey: 'TUTORIAL.STEP_VOIP_HISTORY_TITLE',
      descriptionKey: 'TUTORIAL.STEP_VOIP_HISTORY_DESC',
      position: 'right'
    }
  ]
};

// ============================================================
// PAGE-SPECIFIC TUTORIALS
// ============================================================

// --- Dashboard Manager ---
export const PAGE_DASHBOARD_MANAGER: TutorialModule = {
  id: 'page_dashboard_manager',
  roles: ['ROLE_API_MANAGER'],
  routePrefix: '/manager',
  steps: [
    {
      selector: '.dashboard-container',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.stat-card-panel:nth-child(1)',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_STATS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '.action-grid',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_ACTIONS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_ACTIONS_DESC',
      position: 'bottom'
    },
    {
      selector: '.action-card:nth-child(1)',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_ADD_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_ADD_DESC',
      position: 'right'
    },
    {
      selector: '.toolbar-container',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_SEARCH_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_SEARCH_DESC',
      position: 'bottom'
    }
  ]
};

export const PAGE_CAREGIVER_DASHBOARD: TutorialModule = {
  id: 'page_caregiver_dashboard',
  roles: ['ROLE_API_CAREGIVER'],
  routePrefix: '/caregivers/dashboard-c',
  steps: [
    {
      selector: '.dashboard-container',
      titleKey: 'TUTORIAL.DASHBOARD_CGR_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.stat-card-panel:nth-child(1)',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_STATS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '.action-grid',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_ACTIONS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_CGR_ACTIONS_DESC',
      position: 'bottom'
    },
    {
      selector: '.toolbar-container',
      titleKey: 'TUTORIAL.DASHBOARD_MGR_SEARCH_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_MGR_SEARCH_DESC',
      position: 'bottom'
    }
  ]
};

// --- Dashboard Caregiver ---
export const PAGE_DASHBOARD_CAREGIVER: TutorialModule = {
  id: 'page_dashboard_caregiver',
  roles: ['ROLE_API_MANAGER', 'ROLE_API_CAREGIVER'],
  routePrefix: '/my-dashboard',
  steps: [
    {
      selector: '.events-dashboard',
      titleKey: 'TUTORIAL.DASHBOARD_CG_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_CG_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.metrics-grid',
      titleKey: 'TUTORIAL.DASHBOARD_CG_STATS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_CG_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '.filters-section',
      titleKey: 'TUTORIAL.DASHBOARD_CG_SEARCH_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_CG_SEARCH_DESC',
      position: 'bottom'
    },
    {
      selector: '.alarms-list',
      titleKey: 'TUTORIAL.DASHBOARD_CG_CARDS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_CG_CARDS_DESC',
      position: 'top'
    }
  ]
};

// --- Dashboard Paramedic ---
export const PAGE_DASHBOARD_PARAMEDIC: TutorialModule = {
  id: 'page_dashboard_paramedic',
  roles: ['ROLE_API_CAREGIVER'],
  userTypes: ['Paramedic'],
  routePrefix: '/caregivers/paramedic-dashboard',
  steps: [
    {
      selector: '.paramedic-dashboard',
      titleKey: 'TUTORIAL.DASHBOARD_PARA_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_PARA_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.metrics-grid',
      titleKey: 'TUTORIAL.DASHBOARD_PARA_METRICS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_PARA_METRICS_DESC',
      position: 'bottom'
    },
    {
      selector: '.filters-section',
      titleKey: 'TUTORIAL.DASHBOARD_PARA_FILTERS_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_PARA_FILTERS_DESC',
      position: 'bottom'
    },
    {
      selector: '.table-section',
      titleKey: 'TUTORIAL.DASHBOARD_PARA_TABLE_TITLE',
      descriptionKey: 'TUTORIAL.DASHBOARD_PARA_TABLE_DESC',
      position: 'top'
    }
  ]
};

// --- Caregivers List ---
export const PAGE_CAREGIVERS_LIST: TutorialModule = {
  id: 'page_caregivers_list',
  roles: ['ROLE_API_MANAGER'],
  routePrefix: '/caregivers',
  steps: [
    {
      selector: '.dashboard-container-panel',
      titleKey: 'TUTORIAL.CG_LIST_STATS_TITLE',
      descriptionKey: 'TUTORIAL.CG_LIST_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '.action-grid',
      titleKey: 'TUTORIAL.CG_LIST_ACTIONS_TITLE',
      descriptionKey: 'TUTORIAL.CG_LIST_ACTIONS_DESC',
      position: 'bottom'
    },
    {
      selector: '.search-container',
      titleKey: 'TUTORIAL.CG_LIST_SEARCH_TITLE',
      descriptionKey: 'TUTORIAL.CG_LIST_SEARCH_DESC',
      position: 'bottom'
    },
    {
      selector: '.list-view-button',
      titleKey: 'TUTORIAL.CG_LIST_VIEW_TITLE',
      descriptionKey: 'TUTORIAL.CG_LIST_VIEW_DESC',
      position: 'left'
    },
    {
      selector: '.stacked-card:first-child',
      titleKey: 'TUTORIAL.CG_LIST_CARD_TITLE',
      descriptionKey: 'TUTORIAL.CG_LIST_CARD_DESC',
      position: 'right'
    },
    {
      selector: '.stacked-card:first-child .menu-button',
      titleKey: 'TUTORIAL.CG_LIST_MENU_TITLE',
      descriptionKey: 'TUTORIAL.CG_LIST_MENU_DESC',
      position: 'left'
    }
  ]
};

// --- Add Caregiver ---
export const PAGE_CAREGIVERS_ADD: TutorialModule = {
  id: 'page_caregivers_add',
  roles: ['ROLE_API_MANAGER'],
  routePrefix: '/caregivers/add-caregiver',
  steps: [
    {
      selector: '.modal-container-emergency-form',
      titleKey: 'TUTORIAL.CG_ADD_FORM_TITLE',
      descriptionKey: 'TUTORIAL.CG_ADD_FORM_DESC',
      position: 'left'
    },
    {
      selector: 'app-change-avatar',
      titleKey: 'TUTORIAL.CG_ADD_AVATAR_TITLE',
      descriptionKey: 'TUTORIAL.CG_ADD_AVATAR_DESC',
      position: 'bottom'
    },
    {
      selector: '.form-container-observator',
      titleKey: 'TUTORIAL.CG_ADD_FIELDS_TITLE',
      descriptionKey: 'TUTORIAL.CG_ADD_FIELDS_DESC',
      position: 'left'
    },
    {
      selector: '.form-actions-observator',
      titleKey: 'TUTORIAL.CG_ADD_SUBMIT_TITLE',
      descriptionKey: 'TUTORIAL.CG_ADD_SUBMIT_DESC',
      position: 'top'
    }
  ]
};

// --- Users List ---
export const PAGE_USERS_LIST: TutorialModule = {
  id: 'page_users_list',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  routePrefix: '/users',
  steps: [
    {
      selector: '.dashboard-container-panel',
      titleKey: 'TUTORIAL.USR_LIST_STATS_TITLE',
      descriptionKey: 'TUTORIAL.USR_LIST_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '.action-grid',
      titleKey: 'TUTORIAL.USR_LIST_ACTIONS_TITLE',
      descriptionKey: 'TUTORIAL.USR_LIST_ACTIONS_DESC',
      position: 'bottom'
    },
    {
      selector: '.search-container',
      titleKey: 'TUTORIAL.USR_LIST_SEARCH_TITLE',
      descriptionKey: 'TUTORIAL.USR_LIST_SEARCH_DESC',
      position: 'bottom'
    },
    {
      selector: '.stacked-card:first-child',
      titleKey: 'TUTORIAL.USR_LIST_CARD_TITLE',
      descriptionKey: 'TUTORIAL.USR_LIST_CARD_DESC',
      position: 'right'
    }
  ]
};

// --- Add User ---
export const PAGE_USERS_ADD: TutorialModule = {
  id: 'page_users_add',
  roles: ['ROLE_API_MANAGER'],
  routePrefix: '/users/add-user',
  steps: [
    {
      selector: '.progress-section',
      titleKey: 'TUTORIAL.USR_ADD_PROGRESS_TITLE',
      descriptionKey: 'TUTORIAL.USR_ADD_PROGRESS_DESC',
      position: 'bottom'
    },
    {
      selector: '.form-section',
      titleKey: 'TUTORIAL.USR_ADD_FORM_TITLE',
      descriptionKey: 'TUTORIAL.USR_ADD_FORM_DESC',
      position: 'top'
    },
    {
      selector: '.settings-header',
      titleKey: 'TUTORIAL.USR_ADD_HEADER_TITLE',
      descriptionKey: 'TUTORIAL.USR_ADD_HEADER_DESC',
      position: 'bottom'
    }
  ]
};

// --- User Details (both /users/user/:id and /users/integrate/:id) ---
export const PAGE_USER_DETAILS: TutorialModule = {
  id: 'page_user_details',
  routePrefix: '/users/user',
  steps: [
    {
      selector: '.patient-info-card',
      titleKey: 'TUTORIAL.USR_DETAIL_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.tab-navigation',
      titleKey: 'TUTORIAL.USR_DETAIL_TABS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_TABS_DESC',
      position: 'bottom'
    },
    {
      selector: '.caregiver-section',
      titleKey: 'TUTORIAL.USR_DETAIL_STATUS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_STATUS_DESC',
      position: 'left'
    },
    {
      selector: '.device-panel-action-cards',
      titleKey: 'TUTORIAL.USR_DETAIL_ACTIONS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_ACTIONS_DESC',
      position: 'bottom'
    },
    {
      selector: '.metrics-grid',
      titleKey: 'TUTORIAL.USR_DETAIL_METRICS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_METRICS_DESC',
      position: 'bottom'
    },
    {
      selector: '.schedule-section',
      titleKey: 'TUTORIAL.USR_DETAIL_SYNC_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_SYNC_DESC',
      position: 'bottom'
    },
    {
      selector: '.location-card',
      titleKey: 'TUTORIAL.USR_DETAIL_MAP_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_MAP_DESC',
      position: 'left'
    },
    {
      selector: '.contacts-section',
      titleKey: 'TUTORIAL.USR_DETAIL_CONTACTS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_CONTACTS_DESC',
      position: 'left'
    },
    {
      selector: '.alarms-section',
      titleKey: 'TUTORIAL.USR_DETAIL_ALARMS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_ALARMS_DESC',
      position: 'left'
    },
    {
      selector: '.notes-section',
      titleKey: 'TUTORIAL.USR_DETAIL_NOTES_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_NOTES_DESC',
      position: 'left'
    },
    {
      selector: '.ai-summary-card',
      titleKey: 'TUTORIAL.USR_DETAIL_AI_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_AI_DESC',
      position: 'top'
    },
    {
      selector: '.pulse-day-average',
      titleKey: 'TUTORIAL.USR_DETAIL_PULSE_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_PULSE_DESC',
      position: 'bottom'
    },
    {
      selector: '.sat-day-night-metrics',
      titleKey: 'TUTORIAL.USR_DETAIL_SATURATION_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_SATURATION_DESC',
      position: 'bottom'
    },
    {
      selector: '.band-time-note',
      titleKey: 'TUTORIAL.USR_DETAIL_TIME_ZONE_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_TIME_ZONE_DESC',
      position: 'top'
    }
  ]
};

// --- User Integrate (legacy route) ---
export const PAGE_USER_INTEGRATE: TutorialModule = {
  id: 'page_user_integrate',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  routePrefix: '/users/integrate',
  steps: [
    {
      selector: '.patient-info-card',
      titleKey: 'TUTORIAL.USR_DETAIL_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.caregiver-section',
      titleKey: 'TUTORIAL.USR_DETAIL_STATUS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_STATUS_DESC',
      position: 'left'
    },
    {
      selector: '.alarms-section',
      titleKey: 'TUTORIAL.USR_DETAIL_ALARMS_TITLE',
      descriptionKey: 'TUTORIAL.USR_DETAIL_ALARMS_DESC',
      position: 'top'
    }
  ]
};

// --- Alarms ---
export const FEATURE_ALARMS: TutorialModule = {
  id: 'feature_alarms',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  steps: [
    {
      selector: '.alert-container',
      titleKey: 'TUTORIAL.ALARM_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.ALARM_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.alert-header',
      titleKey: 'TUTORIAL.ALARM_TYPE_TITLE',
      descriptionKey: 'TUTORIAL.ALARM_TYPE_DESC',
      position: 'bottom'
    },
    {
      selector: '.user-section',
      titleKey: 'TUTORIAL.ALARM_USER_TITLE',
      descriptionKey: 'TUTORIAL.ALARM_USER_DESC',
      position: 'right'
    },
    {
      selector: '.device-section',
      titleKey: 'TUTORIAL.ALARM_DEVICE_TITLE',
      descriptionKey: 'TUTORIAL.ALARM_DEVICE_DESC',
      position: 'right'
    },
    {
      selector: '.action-section',
      titleKey: 'TUTORIAL.ALARM_ACTION_TITLE',
      descriptionKey: 'TUTORIAL.ALARM_ACTION_DESC',
      position: 'top'
    }
  ]
};

// --- Projects ---
export const PAGE_PROJECTS: TutorialModule = {
  id: 'page_projects',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  routePrefix: '/projects',
  steps: [
    {
      selector: '.dashboard-container',
      titleKey: 'TUTORIAL.PROJ_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.PROJ_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.stats-container',
      titleKey: 'TUTORIAL.PROJ_STATS_TITLE',
      descriptionKey: 'TUTORIAL.PROJ_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '.table-controls',
      titleKey: 'TUTORIAL.PROJ_SEARCH_TITLE',
      descriptionKey: 'TUTORIAL.PROJ_SEARCH_DESC',
      position: 'bottom'
    },
    {
      selector: '.projects-table',
      titleKey: 'TUTORIAL.PROJ_TABLE_TITLE',
      descriptionKey: 'TUTORIAL.PROJ_TABLE_DESC',
      position: 'top'
    },
    {
      selector: '.right-panel',
      titleKey: 'TUTORIAL.PROJ_PANEL_TITLE',
      descriptionKey: 'TUTORIAL.PROJ_PANEL_DESC',
      position: 'left'
    }
  ]
};

// --- Reports ---
export const PAGE_REPORTS: TutorialModule = {
  id: 'page_reports',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  routePrefix: '/settings/reports',
  steps: [
    {
      selector: '.action-btn',
      titleKey: 'TUTORIAL.REPORT_PAGE_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.REPORT_PAGE_OVERVIEW_DESC',
      position: 'bottom',
    },
    {
      selector: '.history-tab:nth-of-type(1)',
      titleKey: 'TUTORIAL.REPORT_WORK_HISTORY_TITLE',
      descriptionKey: 'TUTORIAL.REPORT_WORK_HISTORY_DESC',
      position: 'bottom',
    },
    {
      selector: '.history-tab:nth-of-type(2)',
      titleKey: 'TUTORIAL.REPORT_CHANGE_HISTORY_TITLE',
      descriptionKey: 'TUTORIAL.REPORT_CHANGE_HISTORY_DESC',
      position: 'bottom',
    },
  ]
};

// --- AI Analysis ---
export const PAGE_AI_ANALYSIS: TutorialModule = {
  id: 'page_ai_analysis',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  privileges: ['hasAiAccess'],
  routePrefix: '/settings/ai-analysis',
  steps: [
    {
      selector: '.table',
      titleKey: 'TUTORIAL.AI_ANALYSIS_TABLE_TITLE',
      descriptionKey: 'TUTORIAL.AI_ANALYSIS_TABLE_DESC',
      position: 'top'
    },
    {
      selector: 'app-pagination',
      titleKey: 'TUTORIAL.AI_ANALYSIS_PAGINATION_TITLE',
      descriptionKey: 'TUTORIAL.AI_ANALYSIS_PAGINATION_DESC',
      position: 'top'
    }
  ]
};

// ============================================================
// FEATURE-SPECIFIC TUTORIALS (VoIP, AI, EKO)
// ============================================================

// --- VoIP Phone ---
export const FEATURE_VOIP_PHONE: TutorialModule = {
  id: 'feature_voip_phone',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  steps: [
    {
      selector: '.phone-panel',
      titleKey: 'TUTORIAL.VOIP_PHONE_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.VOIP_PHONE_OVERVIEW_DESC',
      position: 'left'
    },
    {
      selector: '.workstation-list',
      titleKey: 'TUTORIAL.VOIP_PHONE_WORKSTATION_TITLE',
      descriptionKey: 'TUTORIAL.VOIP_PHONE_WORKSTATION_DESC',
      position: 'left'
    },
    {
      selector: '.dialer-screen',
      titleKey: 'TUTORIAL.VOIP_PHONE_DIALER_TITLE',
      descriptionKey: 'TUTORIAL.VOIP_PHONE_DIALER_DESC',
      position: 'left'
    },
    {
      selector: '.status-bar',
      titleKey: 'TUTORIAL.VOIP_PHONE_STATUS_TITLE',
      descriptionKey: 'TUTORIAL.VOIP_PHONE_STATUS_DESC',
      position: 'left'
    }
  ]
};

// --- AI Paramedic ---
export const FEATURE_AI_PARAMEDIC: TutorialModule = {
  id: 'feature_ai_paramedic',
  roles: ['ROLE_API_MANAGER'],
  routePrefix: '/manager/ai-paramedic',
  steps: [
    {
      selector: '.centrum-polaczen',
      titleKey: 'TUTORIAL.AI_PARA_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.AI_PARA_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.tab-navigation',
      titleKey: 'TUTORIAL.AI_PARA_TABS_TITLE',
      descriptionKey: 'TUTORIAL.AI_PARA_TABS_DESC',
      position: 'bottom'
    },
    {
      selector: '.stats-row',
      titleKey: 'TUTORIAL.AI_PARA_STATS_TITLE',
      descriptionKey: 'TUTORIAL.AI_PARA_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '.search-bar',
      titleKey: 'TUTORIAL.AI_PARA_SEARCH_TITLE',
      descriptionKey: 'TUTORIAL.AI_PARA_SEARCH_DESC',
      position: 'bottom'
    },
    {
      selector: '.action-bar',
      titleKey: 'TUTORIAL.AI_PARA_ACTIONS_TITLE',
      descriptionKey: 'TUTORIAL.AI_PARA_ACTIONS_DESC',
      position: 'bottom'
    },
    {
      selector: '.data-table',
      titleKey: 'TUTORIAL.AI_PARA_TABLE_TITLE',
      descriptionKey: 'TUTORIAL.AI_PARA_TABLE_DESC',
      position: 'top'
    }
  ]
};

// --- Notifications Sidenav ---
export const FEATURE_NOTIFICATIONS: TutorialModule = {
  id: 'feature_notifications',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  steps: [
    {
      selector: '#mySidenav',
      titleKey: 'TUTORIAL.NOTIF_PANEL_TITLE',
      descriptionKey: 'TUTORIAL.NOTIF_PANEL_DESC',
      position: 'left'
    },
    {
      selector: '.notifIcon',
      titleKey: 'TUTORIAL.NOTIF_ICON_TITLE',
      descriptionKey: 'TUTORIAL.NOTIF_ICON_DESC',
      position: 'left'
    },
    {
      selector: '.handleBttnDark',
      titleKey: 'TUTORIAL.NOTIF_HANDLE_TITLE',
      descriptionKey: 'TUTORIAL.NOTIF_HANDLE_DESC',
      position: 'left'
    }
  ]
};

// --- Registration ---
export const PAGE_REGISTER: TutorialModule = {
  id: 'page_register',
  routePrefix: '/auth/register',
  steps: [
    {
      selector: '.auth-container',
      titleKey: 'TUTORIAL.REG_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.REG_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.countries-grid',
      titleKey: 'TUTORIAL.REG_COUNTRY_TITLE',
      descriptionKey: 'TUTORIAL.REG_COUNTRY_DESC',
      position: 'bottom'
    },
    {
      selector: '.continue-button',
      titleKey: 'TUTORIAL.REG_CONTINUE_TITLE',
      descriptionKey: 'TUTORIAL.REG_CONTINUE_DESC',
      position: 'top'
    }
  ]
};

// --- Edit User (Manager/Caregiver view) ---
export const PAGE_EDIT_USER: TutorialModule = {
  id: 'page_edit_user',
  // /users/edit-user jest dostępne też dla zwykłego użytkownika (ROLE_API_USER) —
  // bez tej roli przycisk pomocy/tutoriala na tej stronie nic nie robił.
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER', 'ROLE_API_USER'],
  routePrefix: '/users/edit-user',
  steps: [
    {
      selector: '.settings-nav',
      titleKey: 'TUTORIAL.EDIT_USR_NAV_TITLE',
      descriptionKey: 'TUTORIAL.EDIT_USR_NAV_DESC',
      position: 'right'
    },
    {
      selector: '#profile',
      titleKey: 'TUTORIAL.EDIT_USR_PROFILE_TITLE',
      descriptionKey: 'TUTORIAL.EDIT_USR_PROFILE_DESC',
      position: 'left'
    },
    {
      selector: '.edit-button',
      titleKey: 'TUTORIAL.EDIT_USR_EDIT_TITLE',
      descriptionKey: 'TUTORIAL.EDIT_USR_EDIT_DESC',
      position: 'left'
    },
    {
      selector: '#nav-device',
      titleKey: 'TUTORIAL.EDIT_USR_ALARM_PHONES_TITLE',
      descriptionKey: 'TUTORIAL.EDIT_USR_ALARM_PHONES_DESC',
      position: 'right'
    },
    {
      selector: '#nav-contacts',
      titleKey: 'TUTORIAL.EDIT_USR_CONTACT_PERSONS_TITLE',
      descriptionKey: 'TUTORIAL.EDIT_USR_CONTACT_PERSONS_DESC',
      position: 'right'
    }
  ]
};

// --- Account Settings (Caregiver/Manager) ---
export const PAGE_SETTINGS_ACCOUNT: TutorialModule = {
  id: 'page_settings_account',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  routePrefix: '/settings/configuration',
  steps: [
    {
      selector: '.settings-frame',
      titleKey: 'TUTORIAL.SETTINGS_ACC_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.SETTINGS_ACC_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.settings-nav',
      titleKey: 'TUTORIAL.SETTINGS_ACC_NAV_TITLE',
      descriptionKey: 'TUTORIAL.SETTINGS_ACC_NAV_DESC',
      position: 'right'
    },
    {
      selector: '.profile-section',
      titleKey: 'TUTORIAL.SETTINGS_ACC_PROFILE_TITLE',
      descriptionKey: 'TUTORIAL.SETTINGS_ACC_PROFILE_DESC',
      position: 'left'
    },
    {
      selector: '.edit-button',
      titleKey: 'TUTORIAL.SETTINGS_ACC_EDIT_TITLE',
      descriptionKey: 'TUTORIAL.SETTINGS_ACC_EDIT_DESC',
      position: 'left'
    }
  ]
};

// --- Individual User Settings ---
export const PAGE_INDIV_USER_SETTINGS: TutorialModule = {
  id: 'page_indiv_user_settings',
  roles: ['ROLE_API_USER'],
  requireIndividual: true,
  routePrefix: '/indiv-client/edit-user',
  steps: [
    {
      selector: '.settings-frame',
      titleKey: 'TUTORIAL.INDIV_SETTINGS_OVERVIEW_TITLE',
      descriptionKey: 'TUTORIAL.INDIV_SETTINGS_OVERVIEW_DESC',
      position: 'bottom'
    },
    {
      selector: '.settings-nav',
      titleKey: 'TUTORIAL.INDIV_SETTINGS_NAV_TITLE',
      descriptionKey: 'TUTORIAL.INDIV_SETTINGS_NAV_DESC',
      position: 'right'
    },
    {
      selector: '#profile',
      titleKey: 'TUTORIAL.INDIV_SETTINGS_PROFILE_TITLE',
      descriptionKey: 'TUTORIAL.INDIV_SETTINGS_PROFILE_DESC',
      position: 'left'
    },
    {
      selector: '.device-settings',
      titleKey: 'TUTORIAL.INDIV_SETTINGS_DEVICE_TITLE',
      descriptionKey: 'TUTORIAL.INDIV_SETTINGS_DEVICE_DESC',
      position: 'left'
    },
    {
      selector: '.imei-section',
      titleKey: 'TUTORIAL.INDIV_SETTINGS_IMEI_TITLE',
      descriptionKey: 'TUTORIAL.INDIV_SETTINGS_IMEI_DESC',
      position: 'bottom'
    },
    {
      selector: '.device-screens',
      titleKey: 'TUTORIAL.INDIV_SETTINGS_SCREENS_TITLE',
      descriptionKey: 'TUTORIAL.INDIV_SETTINGS_SCREENS_DESC',
      position: 'left'
    },
    {
      selector: '.security-settings',
      titleKey: 'TUTORIAL.INDIV_SETTINGS_SECURITY_TITLE',
      descriptionKey: 'TUTORIAL.INDIV_SETTINGS_SECURITY_DESC',
      position: 'left'
    },
    {
      selector: '#nav-device',
      titleKey: 'TUTORIAL.EDIT_USR_ALARM_PHONES_TITLE',
      descriptionKey: 'TUTORIAL.EDIT_USR_ALARM_PHONES_DESC',
      position: 'right'
    },
    {
      selector: '#nav-contacts',
      titleKey: 'TUTORIAL.EDIT_USR_CONTACT_PERSONS_TITLE',
      descriptionKey: 'TUTORIAL.EDIT_USR_CONTACT_PERSONS_DESC',
      position: 'right'
    }
  ]
};

// --- Alarm Zone Config ---
export const PAGE_ALARM_ZONE: TutorialModule = {
  id: 'page_alarm_zone',
  roles: ['ROLE_API_CAREGIVER', 'ROLE_API_MANAGER'],
  steps: [
    {
      selector: '.form-group',
      titleKey: 'TUTORIAL.ZONE_ADDRESS_TITLE',
      descriptionKey: 'TUTORIAL.ZONE_ADDRESS_DESC',
      position: 'right'
    },
    {
      selector: 'app-slider:nth-of-type(1)',
      titleKey: 'TUTORIAL.ZONE_DISTANCE_TITLE',
      descriptionKey: 'TUTORIAL.ZONE_DISTANCE_DESC',
      position: 'right'
    },
    {
      selector: 'agm-map',
      titleKey: 'TUTORIAL.ZONE_MAP_TITLE',
      descriptionKey: 'TUTORIAL.ZONE_MAP_DESC',
      position: 'top'
    }
  ]
};

// --- Caregiver Profile (manager viewing a caregiver: home / dependents / events tabs) ---
// routePrefix is more specific than '/caregivers' so it wins over PAGE_CAREGIVERS_LIST.
// .profile-card and .tab-navigation are always present, so the tour shows something on
// every tab; the stats/events steps auto-skip on tabs where their element is absent.
export const PAGE_CAREGIVER_PROFILE: TutorialModule = {
  id: 'page_caregiver_profile',
  roles: ['ROLE_API_MANAGER'],
  routePrefix: '/caregivers/caregiver-c',
  steps: [
    {
      selector: '.profile-card',
      titleKey: 'TUTORIAL.CG_PROFILE_HEADER_TITLE',
      descriptionKey: 'TUTORIAL.CG_PROFILE_HEADER_DESC',
      position: 'bottom'
    },
    {
      selector: '.tab-navigation',
      titleKey: 'TUTORIAL.CG_PROFILE_TABS_TITLE',
      descriptionKey: 'TUTORIAL.CG_PROFILE_TABS_DESC',
      position: 'bottom'
    },
    {
      selector: '#general-panel .dashboard-container-panel',
      titleKey: 'TUTORIAL.CG_PROFILE_STATS_TITLE',
      descriptionKey: 'TUTORIAL.CG_PROFILE_STATS_DESC',
      position: 'bottom'
    },
    {
      selector: '#events-panel .controls-section-notifs',
      titleKey: 'TUTORIAL.CG_PROFILE_EVENTS_TITLE',
      descriptionKey: 'TUTORIAL.CG_PROFILE_EVENTS_DESC',
      position: 'bottom'
    }
  ]
};

// ============================================================
// ALL MODULES REGISTRY
// ============================================================

export const ALL_TUTORIAL_MODULES: TutorialModule[] = [
  // Onboarding
  ONBOARDING_MANAGER,
  ONBOARDING_CAREGIVER,
  // Dashboards
  PAGE_DASHBOARD_MANAGER,
  PAGE_DASHBOARD_CAREGIVER,
  PAGE_CAREGIVER_DASHBOARD,
  PAGE_DASHBOARD_PARAMEDIC,
  // Caregivers
  PAGE_CAREGIVERS_LIST,
  PAGE_CAREGIVER_PROFILE,
  PAGE_CAREGIVERS_ADD,
  // Users
  PAGE_USERS_LIST,
  PAGE_USERS_ADD,
  PAGE_USER_DETAILS,
  PAGE_USER_INTEGRATE,
  // Alarms
  FEATURE_ALARMS,
  PAGE_ALARM_ZONE,
  // Projects
  PAGE_PROJECTS,
  // Reports
  PAGE_REPORTS,
  PAGE_AI_ANALYSIS,
  // VoIP
  FEATURE_VOIP_PHONE,
  // AI
  FEATURE_AI_PARAMEDIC,
  // Edit User
  PAGE_EDIT_USER,
  // Settings
  PAGE_SETTINGS_ACCOUNT,
  PAGE_INDIV_USER_SETTINGS,
  // Notifications
  FEATURE_NOTIFICATIONS,
  // Individual
  // Registration
  PAGE_REGISTER
];
